// TabContent1.js
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ProgressionTab from '../../../../theme/ProgressionBuilder/ProgressionTab';
import NumberInput from '../../../../theme/inputs/NumberInput';
import { useTheme } from '@emotion/react';
import Text from '../../../../theme/text/Text';
import { useTranslation } from 'react-i18next';
import { Box, InputBase, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { tokens } from '../../../../../theme';
import { useGetProductsSelectQuery } from '../../../../../newapi/warehouse/productSlice';
import ShValidatedInputDropdown from '../../../../theme/dropdowns/ShValidatedInputDropdown';
import { setTabErrors } from '../../../../../newapi/global/progressionTabSlice';
import { useDispatch } from 'react-redux';

/**
 * TabContent1 Component
 * 
 * Props:
 * - handleDataUpdate: Function, updates the data in the Redux state.
 * - handleNext: Function, navigates to the next tab.
 */
const Content = (props) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const colors = tokens(theme.palette.mode);
    const data = { ...props.data };
    const dispatch = useDispatch();
    
    const [newOrderlines, setNewOrderlines] = useState([{
        sku: "",
        amount: 0,
        index: 1
    }]);
    const previousOrderlinesRef = useRef();
    const isInitializedRef = useRef(false);
    const initialOrderlinesRef = useRef(null);

    const { data: products } = useGetProductsSelectQuery(
        {
            // webshopcredentialsId: data.webshopCredentialsId,
            relationId:data.relationId,
            addStock: true,
            ignoreAddon: true
        },
        {
            skip: !data.relationId
        }
    );

    const productOptions = useMemo(() => {
        if (!products) return [];
        return products.map(product => ({
            id: product.sku,
            name: product.combined,
            barcodes:product.barcodes,
            isVirtual: product.isVirtual
        }));
    }, [products]);
    
   // Store initial orderlines data when it first arrives, filtering out child products
    useEffect(() => {
        if (data.orderlines && data.orderlines.length > 0 && !initialOrderlinesRef.current) {
            // Filter out orderlines that reference child products (products with parentProductId > 0)
            const filteredOrderlines = data.orderlines.filter(orderline => {
                if (!products) return true; // Keep all orderlines if products not loaded yet
                const product = products.find(p => p.sku === orderline.sku);
                return !product || product.isVirtual;
            });
            initialOrderlinesRef.current = [...filteredOrderlines];
        }
    }, [data.orderlines, products]);

    useEffect(() => {
        
        // Only initialize once when products are loaded and we haven't initialized yet
        if (!products || !productOptions.length || isInitializedRef.current) return;
        
        // Use initial orderlines if current data.orderlines is empty but we have stored initial data
        const orderlinesData = (data.orderlines && data.orderlines.length > 0) 
            ? data.orderlines 
            : initialOrderlinesRef.current;
        
        
        // Set the newOrderlines state to the orderlines data
        if (orderlinesData && orderlinesData.length > 0) {
            const linesCopy = orderlinesData.map((line, index) => ({
                ...line,
                sku: line.sku || line.id || "",  // Use line.id if line.sku doesn't exist
                amount: line.amount || 1,
                index: index
            }));
            
            linesCopy.push({
                sku: "",
                amount: 1,
                index: linesCopy.length
            });

            setNewOrderlines(linesCopy);
        }
        
        isInitializedRef.current = true;
    }, [products, productOptions.length, data.orderlines]);

    // Reset initialization flag when orderlines data changes significantly
    useEffect(() => {
        if (data.orderlines && data.orderlines.length > 0 && isInitializedRef.current) {
            // Check if the current newOrderlines don't match the data.orderlines
            const currentValidOrderlines = newOrderlines.filter(x => x && x.sku && x.sku.length > 0);
            if (currentValidOrderlines.length !== data.orderlines.length) {
                isInitializedRef.current = false;
            }
        }
    }, [data.orderlines, newOrderlines]);

    const handleChange = (index, key, value) => {
        if (!newOrderlines || !Array.isArray(newOrderlines)) return;
        
        const orderlinesCopy = [...newOrderlines];
        if (!orderlinesCopy[index]) return;
        
        orderlinesCopy[index][key] = value;

        const lastOrderline = orderlinesCopy[orderlinesCopy.length - 1];
        if (lastOrderline && lastOrderline.sku && lastOrderline.sku.length > 0) {
            orderlinesCopy.push({
                sku: "",
                amount: 1,
                index: orderlinesCopy.length
            });
        }

        setNewOrderlines(orderlinesCopy);
    };
    
    const mappedOrderlines = useMemo(() => {
        if(!newOrderlines || !Array.isArray(newOrderlines)) return [];
        return newOrderlines
            .filter(x => (x && x.sku && x.sku.length > 0 && x.amount > 0))
            .map(x => ({
                sku: x.sku,
                amount: x.amount
            }));
    }, [newOrderlines]);

    useEffect(() => {
        // Only update if the mapped orderlines have actually changed
        const previousOrderlines = previousOrderlinesRef.current;
        const hasChanged = JSON.stringify(mappedOrderlines) !== JSON.stringify(previousOrderlines);
        
        if (hasChanged) {
            previousOrderlinesRef.current = mappedOrderlines;
            handleInput("orderlines", mappedOrderlines);
        }
        
    }, [mappedOrderlines]);
      
    const handleDeleleteLineRow = (index) => {
        const orderlinesCopy = [...newOrderlines];
        orderlinesCopy.splice(index, 1);
        setNewOrderlines(orderlinesCopy);
    }

    const handleProductSelect = (key, val) => {
        const lineIndex = key.split(".")[0];
        const lineKey = key.split(".")[1];
        handleChange(lineIndex, lineKey, val);
    }

    const handleInput = useCallback((key, val) => {
        props.handleDataUpdate({ [key]: val });
        dispatch(setTabErrors({ ...props.errors, [key]: '' }));
    }, [props.handleDataUpdate, props.errors, dispatch]);

    return (
        <Box
            sx={{
                background:theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                borderTopLeftRadius:16,
                borderTopRightRadius:16,
                display:"flex",
                flexDirection:"column",
                gap:1,
            }}
        >
            {newOrderlines.map((orderline, index) => (
                <Box flex={1} key={"new-orderline-"+index}
                    sx={{
                        display:"flex",
                        gap:4,
                        justifyContent:"flex-start",
                    }}
                >
                    <Box 
                        sx={{
                            width:1,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"space-between",
                            gap:1
                        }}
                    >
                        <Box
                            sx={{
                                width:80,
                            }}
                        >
                            <Text light>{t("sku")}</Text>
                        </Box>
                        <Box
                            sx={{
                                flex:1,
                                backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                                borderRadius:4,
                                display:"flex",
                                height:32
                            }}
                        >
                            <ShValidatedInputDropdown
                                key={"new-orderline-product-"+index}
                                displayName={"name"} 
                                changeField={"id"}
                                valueKey={"id"}
                                closeOnSelection
                                searchOptions={{
                                    enabled: true,
                                    placeholder: t("search_product_placeholder"),
                                    searchables: ["barcodes", "id"]
                                }}
                                styles={{
                                    minWidth:300
                                }}

                                disallowDeselection
                                name={`${index}.sku`}
                                selected={(() => {
                                    const foundProduct = productOptions?.find(item => item.id && item.id === orderline.sku);
                                    return foundProduct?.id ?? -1;
                                })()}
                                options={productOptions ? productOptions : []}
                                noSelection={t("choose_option")} 
                                onChange={handleProductSelect}
                                error={props.errors.sku}
                            />
                        </Box>
                    </Box>
                    <Box 
                        sx={{
                            width:280,
                            display:"flex",
                            alignItems:"center",
                            justifyContent:"space-between",
                            gap:1
                        }}
                    >
                        <Box
                            sx={{
                                width:64,
                            }}
                        >
                            <Text light>{t("amount")}</Text>
                        </Box>
                        <Box
                            sx={{
                                flex:1,
                                width:144,
                                backgroundColor : `${theme.palette.mode === "dark" ?colors.grey[300] : colors.grey[100]}`,
                                borderRadius:4,
                                display:"flex",
                                height:32
                            }}
                        >
                            <NumberInput btnSize={"24"}
                                value={orderline.amount}
                                change={(e) => handleChange(index, 'amount', e)}/>
                        </Box>

                        { index > 0 ?
                        <Box sx={{width:40,display:"flex",justifyContent:"flex-end"}}>
                        <IconButton type="button" color="red" onClick={() => handleDeleleteLineRow(index)} sx={{p:1}}>
                            <DeleteIcon/>
                        </IconButton>
                        </Box> 
                        : <Box width={40}/>
                        }
                    </Box>
                </Box>
            ))}
        </Box>
    );
};

const Orderlines = (props) => {
    const {t} = useTranslation();
    return (
        <ProgressionTab
        tabId="tabOrderlines"
        subtitle={t("orderlines")}
        step={3}
        maxSteps={3}
        allowSaveAndOpen
    >
        <Content {...props} />
    </ProgressionTab>
    )
}

export default Orderlines;

